import {
  Directive,
  HostListener,
  Input,
  ElementRef,
  OnInit,
} from '@angular/core';

@Directive({
  selector: '[onlyNumbers][onlyNumbersAllowDash]'
})
export class onlyNumbersDirective implements OnInit {
  @Input() onlyNumbersAllowDash = false;
  @Input() onlyNumbersAllowPlus = false;

  constructor(private el: ElementRef<HTMLInputElement>) {}

  ngOnInit(): void {}

  @HostListener('input', ['$event'])
  onInputChange(event: any): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    // Build allowed characters dynamically
    let allowedChars = '0-9';
    if (this.onlyNumbersAllowDash) allowedChars += '-';
    if (this.onlyNumbersAllowPlus) allowedChars += '\\+';

    // Remove invalid characters
    const regex = new RegExp(`[^${allowedChars}]`, 'g');
    value = value.replace(regex, '');

    // Handle dash: only one, not at start/end
    if (this.onlyNumbersAllowDash) {
      const dashCount = (value.match(/-/g) || []).length;
      if (dashCount > 1) value = value.replace(/-/g, '');
      if (value.startsWith('-')) value = value.replace(/^-/, '');
      if (value.endsWith('-')) value = value.replace(/-$/, '');
    }

    // Handle plus: only at the start, only one
    if (this.onlyNumbersAllowPlus) {
      const plusCount = (value.match(/\+/g) || []).length;
      if (plusCount > 1) value = value.replace(/\+/g, '');
      if (!value.startsWith('+')) value = value.replace(/\+/g, '');
    }

    if (input.value !== value) {
      input.value = value;
      event.target.dispatchEvent(new Event('input'));
    }
  }

  @HostListener('beforeinput', ['$event'])
  onBeforeInput(event: InputEvent): void {
    if (event.inputType !== 'insertText' || !event.data) return;

    const input = this.el.nativeElement;
    const currentValue = input.value;
    const selectionStart = input.selectionStart || 0;
    const selectionEnd = input.selectionEnd || 0;

    const nextValue =
      currentValue.slice(0, selectionStart) +
      event.data +
      currentValue.slice(selectionEnd);

    let allowedChars = '0-9';
    if (this.onlyNumbersAllowDash) allowedChars += '-';
    if (this.onlyNumbersAllowPlus) allowedChars += '\\+';

    const regex = new RegExp(`^[${allowedChars}]*$`);
    if (!regex.test(event.data)) {
      event.preventDefault();
      return;
    }

    // Dash rules
    if (this.onlyNumbersAllowDash) {
      const dashCount = (nextValue.match(/-/g) || []).length;
      const isDash = event.data === '-';
      if (
        (isDash && dashCount > 1) ||
        (isDash && selectionStart === 0) ||
        (isDash && selectionStart === nextValue.length)
      ) {
        event.preventDefault();
        return;
      }
    }

    // Plus rules
    if (this.onlyNumbersAllowPlus) {
      const plusCount = (nextValue.match(/\+/g) || []).length;
      const isPlus = event.data === '+';
      if (isPlus && (plusCount > 1 || selectionStart !== 0)) {
        event.preventDefault();
        return;
      }
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const clipboardData = event.clipboardData?.getData('text/plain') || '';
    let sanitizedValue = clipboardData;

    let allowedChars = '0-9';
    if (this.onlyNumbersAllowDash) allowedChars += '-';
    if (this.onlyNumbersAllowPlus) allowedChars += '\\+';

    sanitizedValue = sanitizedValue.replace(new RegExp(`[^${allowedChars}]`, 'g'), '');

    // Dash rules
    if (this.onlyNumbersAllowDash) {
      const dashCount = (sanitizedValue.match(/-/g) || []).length;
      if (dashCount > 1) sanitizedValue = sanitizedValue.replace(/-/g, '');
      if (sanitizedValue.startsWith('-')) sanitizedValue = sanitizedValue.replace(/^-/, '');
      if (sanitizedValue.endsWith('-')) sanitizedValue = sanitizedValue.replace(/-$/, '');
    }

    // Plus rules
    if (this.onlyNumbersAllowPlus) {
      const plusCount = (sanitizedValue.match(/\+/g) || []).length;
      if (plusCount > 1) sanitizedValue = sanitizedValue.replace(/\+/g, '');
      if (!sanitizedValue.startsWith('+')) sanitizedValue = sanitizedValue.replace(/\+/g, '');
    }

    document.execCommand('insertText', false, sanitizedValue);
  }
}
